export type Refs = Record<string, Element | Element[]>;

/**
 * Base class for custom web components.
 *
 * Manages references to child elements with `ref` attributes and provides
 * automatic cleanup of event listeners via AbortController.
 */
export class Component<
  TRefs extends object = Refs,
  TRequired extends keyof TRefs = never,
> extends HTMLElement {
  #abortController: AbortController | null = null;

  refs = {} as TRefs & { [K in TRequired]-?: NonNullable<TRefs[K]> };

  get signal(): AbortSignal {
    if (!this.#abortController) {
      throw new Error("Cannot access signal before connectedCallback");
    }
    return this.#abortController.signal;
  }

  /**
   * Names of refs the markup must render for the component to work. Subclasses
   * should override with `as const` to narrow the value type, e.g.:
   *
   *   protected requiredRefs = ["track", "slider"] as const;
   *
   * After `super.connectedCallback()` runs, any missing ref throws with a
   * useful message. The same names should be passed as the `TRequired` generic
   * so the listed refs are typed non-undefined across the subclass.
   */
  protected requiredRefs: readonly TRequired[] = [];

  connectedCallback(): void {
    this.#abortController = new AbortController();
    this.#updateRefs();
    this.#assertRequired();
  }

  #assertRequired(): void {
    for (const key of this.requiredRefs) {
      const value = (this.refs as TRefs)[key];
      const missing = value == null || (Array.isArray(value) && value.length === 0);
      if (missing) {
        throw new Error(
          `<${this.tagName.toLowerCase()}>: required ref "${String(key)}" is missing from markup`,
        );
      }
    }
  }

  disconnectedCallback(): void {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Updates the `refs` object by querying all descendant elements with `ref`
   * attributes and storing references to them.
   *
   * Use `ref="name"` for single elements or `ref="name[]"` for arrays.
   */
  #updateRefs(): void {
    const refs: Refs = {};

    for (const element of this.querySelectorAll("[ref]")) {
      const refName = element.getAttribute("ref") ?? "";
      const isArray = refName.endsWith("[]");
      const key = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const existing = refs[key];
        const list = Array.isArray(existing) ? existing : [];
        list.push(element);
        refs[key] = list;
      } else {
        refs[key] = element;
      }
    }

    // TRefs is the subclass's contract about which named refs the
    // template renders; runtime population is DOM-driven and cannot be
    // statically verified, so this is a true boundary cast. The
    // required-key narrowing on `this.refs` is backed by #assertRequired
    // running immediately after.
    this.refs = refs as unknown as typeof this.refs;
  }
}

/**
 * Factory for components with a single source of truth for required refs.
 * The inferred readonly tuple flows into both the runtime check
 * (`requiredRefs`) and the type narrowing on `this.refs`.
 *
 *   class HighlightCraft extends defineComponent<HighlightCraftRefs>()([
 *     "track", "slider", "largeContainer", "titleDisplay",
 *   ]) { ... }
 *
 * The two-call shape is required because TypeScript cannot partially
 * infer one type argument while another is explicit: `TRefs` must be
 * supplied, `TRequired` is inferred from the array literal.
 *
 * Generic base classes cannot use this factory — TypeScript rejects
 * "base class expressions cannot reference class type parameters" for
 * `class Foo<T> extends defineComponent<T>()(...)`. Such classes must
 * extend `Component<T, "...">` directly and declare `requiredRefs`
 * explicitly. PopupDialog is the one in-tree example.
 */
export function defineComponent<TRefs extends object>() {
  return <const TRequired extends readonly (keyof TRefs)[]>(requiredRefs: TRequired) => {
    return class extends Component<TRefs, TRequired[number]> {
      protected requiredRefs = requiredRefs;
    };
  };
}
